/** Light brace-based reindenter (4-space). Not a real formatter. */
export function reindentPhp(code: string): string {
  const lines = code.replace(/\r/g, '').split('\n');
  let depth = 0;
  const out: string[] = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (/^[}\)\]]/.test(line)) depth = Math.max(0, depth - 1);
    if (line.startsWith('<?') || line.startsWith('?>') || line === '') out.push(line);
    else out.push('    '.repeat(depth) + line);
    const opens = (line.match(/[{\[(]/g) || []).length;
    const closes = (line.match(/[}\])]/g) || []).length;
    if (/^[}\)\]]/.test(line)) depth += opens - (closes - 1);
    else depth += opens - closes;
    depth = Math.max(0, depth);
  }
  return out.join('\n');
}

/** First meaningful line of code, for history titles. */
export function firstLine(code: string): string {
  const lines = code
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith('<?') && !l.startsWith('//') && !l.startsWith('#') && !l.startsWith('/*'));
  const s = lines[0] || 'PHP snippet';
  return s.length > 34 ? s.slice(0, 34) + '…' : s;
}

export type FormatEngine = 'prettier' | 'reindent';

/** Format PHP source for the editor. Tries the real prettier-php formatter
 * (lazy-loaded ~104 KB-gzip chunk — see format-php-prettier.ts); falls back to
 * the light `reindentPhp` when:
 *   - the source is mixed PHP/HTML (prettier-php is unstable there), or
 *   - the chunk fails to load (e.g. offline first run), or
 *   - PHP has a syntax error prettier cannot parse.
 * Always resolves — never throws. The returned `engine` lets the UI note when it
 * fell back to the reduced formatter. */
export async function formatPhp(
  code: string,
  phpVersion: string,
): Promise<{ code: string; engine: FormatEngine }> {
  if (looksHtmlHeavy(code)) return { code: reindentPhp(code), engine: 'reindent' };
  try {
    const { formatWithPrettier } = await import('./format-php-prettier');
    const formatted = await formatWithPrettier(code, toPrettierPhpVersion(phpVersion));
    return { code: formatted, engine: 'prettier' };
  } catch (err) {
    // Silent fallback is the product choice, but it would also hide genuine bugs;
    // surface them in dev only (tree-shaken from prod). Behavior is unchanged.
    if (import.meta.env?.DEV) console.warn('prettier-php format failed; using reindenter', err);
    return { code: reindentPhp(code), engine: 'reindent' };
  }
}

/** Map an app PHP version (7.2–8.5) to a value the prettier plugin accepts
 * (7.4, 8.0–8.4); newer/older clamp to the nearest supported. */
function toPrettierPhpVersion(v: string): string {
  const n = Number.parseFloat(v);
  if (!Number.isFinite(n)) return '8.4';
  if (n >= 8.4) return '8.4';
  if (n <= 7.4) return '7.4';
  return v;
}

/** Heuristic: does the source carry meaningful HTML OUTSIDE its PHP tags?
 * prettier-php is unstable on mixed PHP/HTML, so such files route to the safe
 * reindenter. Strips <?php..?> / <?=..?> / bare <?..?> (incl. <?xml, deliberately
 * lumped in) and an unclosed open-tag to EOF, then flags when ≥2 HTML tags remain
 * — conservative (prefers a false "mixed" over mangling real markup). */
function looksHtmlHeavy(code: string): boolean {
  const outside = code.replace(/<\?(?:php|=)?[\s\S]*?(?:\?>|$)/gi, '');
  const tags = outside.match(/<[a-z!][^>]*>/gi);
  return !!tags && tags.length >= 2;
}
